// SlaBar — live 1px progress bar that recomputes from createdAt + severity.
// Re-renders every 30s via a self-managed timer; also reacts when the parent
// re-mounts (e.g. when React Query invalidates after an SLA_WARNING WS event).
import React, { useEffect, useState } from "react";

// Resolution SLA in minutes per severity (mirrors backend sla_monitor.SLA_RESOLVE_MIN)
const SLA_RESOLVE_MIN = { s1: 240, s2: 480, s3: 1440, s4: 2880 };

function computePct(createdAt, severity) {
  if (!createdAt) return null;
  const ts = Date.parse(createdAt);
  if (Number.isNaN(ts)) return null;
  const mins = (Date.now() - ts) / 60000;
  const sla = SLA_RESOLVE_MIN[(severity || "s3").toLowerCase()] || 1440;
  return { pct: (mins / sla) * 100, mins, sla };
}

export const SlaBar = ({ createdAt, severity, height = 2, status }) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, []);

  // Closed/resolved incidents stop the timer
  const finished = ["resolved", "closed", "draft"].includes((status || "").toLowerCase());

  const data = computePct(createdAt, severity);
  if (!data || finished) {
    return <div data-testid="sla-bar-idle" style={{ height, width: "100%" }} />;
  }

  const { pct, mins, sla } = data;
  const remaining = Math.max(0, sla - mins);
  const clamped = Math.min(100, pct);
  const color = pct >= 100 ? "#E24B4A" : pct >= 75 ? "#f3b14a" : "#4ed084";
  const animClass = pct >= 100 ? "sla-flash" : pct >= 75 ? "sla-pulse" : "";

  const tooltip = `SLA: ${pct.toFixed(0)}% elapsed · ${
    pct >= 100
      ? `${Math.round(mins - sla)} min breached`
      : `${Math.round(remaining)} min remaining`
  }`;

  return (
    <div
      title={tooltip}
      style={{
        position: "relative",
        height,
        width: "100%",
        background: "rgba(255,255,255,0.04)",
        overflow: "hidden",
      }}
      data-testid="sla-bar"
      data-pct={pct.toFixed(0)}
      data-state={pct >= 100 ? "breach" : pct >= 75 ? "warning" : "ok"}
    >
      <div
        className={animClass}
        style={{
          height: "100%",
          width: `${clamped}%`,
          background: color,
          transition: "width 600ms ease, background 300ms ease",
        }}
      />
    </div>
  );
};

// Eat-as-you-go: keep the animation CSS as a tiny injected stylesheet so we
// don't have to touch the global index.css.
if (typeof document !== "undefined" && !document.getElementById("sla-bar-css")) {
  const style = document.createElement("style");
  style.id = "sla-bar-css";
  style.textContent = `
    @keyframes sla-pulse-kf { 0%,100% { opacity: 1; } 50% { opacity: 0.6; } }
    @keyframes sla-flash-kf { 0%,100% { opacity: 1; } 50% { opacity: 0.25; } }
    .sla-pulse { animation: sla-pulse-kf 1.8s ease-in-out infinite; }
    .sla-flash { animation: sla-flash-kf 0.8s ease-in-out infinite; }
  `;
  document.head.appendChild(style);
}
